/**
 * IMPORTS
 */
import Store from 'src/store';


/**
 * TYPES
 */
import {IAccount} from 'src/aggregates/accounts/state.d';
import {IAccountById} from 'src/aggregates/accounts/state.d';
import {IAccountState} from 'src/aggregates/accounts/state.d';
import {ITemplate} from 'src/aggregates/accounts/state.d';
import {MessageTypes} from 'src/aggregates/conversations/message/messages.d';


/**
 * CODE
 */

/**
 * I get an account from a given id.
 *
 * :param id: account id to be fetched
 *
 * :returns: account
 */
function get(id: number): IAccount | null
{
    // get account of given id
    const account = getState().byId[id];

    // account does not exist: return null
    if (account === undefined)
    {
        return null;
    }

    // account exists: return it
    return account;
}


/**
 * I get all active accounts.
 *
 * :returns: accounts by id
 */
function getAll (): IAccountById
{
    return getState().byId;
}


/**
 * I get the first account of the given channel.
 *
 * :param channel: channel type
 *
 * :returns: promise with account or null
 */
function getByChannel (channel: string): IAccount | null
{
    // get first account of the desired channel
    const account = Object.values(getState().byId).find(
        (account): boolean => (account.channel === channel),
    );

    // account exists: return it
    if (account !== undefined)
    {
        return account;
    }

    // account does not exist: return null
    return null;
}


/**
 * I get all accounts of an asked channel.
 *
 * :param state: accounts state
 * :param channel: desired channel
 *
 * :returns: promise with account or null
 */
function getAllByChannel(channel: string): IAccount[]
{
    // get accounts of the desired channel and return
    return Object.values(getState().byId).filter(
        (account: IAccount): boolean => (account.channel === channel),
    );
}


/**
 * I get all channels of all accounts.
 *
 * :returns: promise with accounts channels
 */
function getChannels (): string[]
{
    // get all accounts
    const accounts: IAccount[] = Object.values(getState().byId);

    // get each account channel
    const channels: string[] = [];
    for (const account of accounts)
    {
        channels.push(account.channel);
    }

    // FIXME: we should not remove duplicateds here to support multiple accounts
    //        for the same channel.
    //
    // return unique channels
    return Array.from(new Set(channels));
}


/**
 * I get all payloads of an asked channel
 *
 * :param channel: channel to get the payloads
 *
 * :returns: channel payloads
 */
function getChannelPayloads (channel: string): string[]
{
    // get all channels by type
    const channelsByType = getState().channels.byType;

    // get channel payloads
    const payloads = channelsByType[channel]?.payloads;

    // return payloads or empty array if not defined
    return payloads ?? [];
}


/**
 * I get accounts aggregate state.
 *
 * :returns: accounts aggregate state
 */
function getState(): IAccountState
{
    return Store.getState().account;
}


/**
 * I get the templates of a given account.
 *
 * :param accountId: account id
 *
 * :returns: account's templates
 */
function getTemplates (accountId: number): ITemplate[]
{
    // get account
    const account = get(accountId);

    // return templates
    return account?.templates ?? [];
}


/**
 * I get the template identified by an id
 *
 * :param templateId: template id
 *
 * :returns: template information
 */
function getTemplateById (templateId: string): ITemplate | null
{
    // get account templates
    const templates = getState().templates;

    // return templates
    return templates[templateId] ?? null;
}


/**
 * I check if payload type is forwardable.
 *
 * :returns: wheter payload type is forwardable
 */
function isForwardableToChannel(
    channel: string,
    payload: MessageTypes,
): boolean
{
    // get payloads supported by the recipient channel
    const payloads = getState().channels.byType[channel].payloads;

    // coudn't find payloads: return false
    if (payloads === undefined)
    {
        return false;
    }

    // check if payload is supported by recipient channel and return
    return payloads.includes(payload);
}


/**
 * EXPORTS
 */
export default {
    get,
    getAll,
    getAllByChannel,
    getByChannel,
    getChannelPayloads,
    getChannels,
    getTemplateById,
    getTemplates,
    isForwardableToChannel,
};
